import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { CommunityDevelopmentNotesForm } from "./communityDevelopmentNotesForm";
import { CommunityDevelopmentReplyForm } from "./CommunitydevelopmentReplyForm";
import { CommunityDevelopmentPage } from "./communityDevelopmentPage";
import { CommunityDevelopmentService } from "./communityDevelopmentService";
import { LoadingIndicator } from "../../../assets/vfx/loadingIndicator";
import {
    CommunityDevelopmentDto,
    CommunityDevelopmentFavoriteDto,
    CommunityDevelopmentNoteDto,
    CommunityDevelopmentReplyDto,
} from "../../../services/dto";
import { Dialog } from "../../../assets/vfx";
import { CommunityDevelopmentReplyView } from "./communityDevelopmentReplyView";
import { CommunityDevelopmentForm } from "./communityDevelopmentForm";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

interface CommunityDevelopmentContainerProps {
    communityId?: string | undefined;
}

export const CommunityDevelopmentContainer = (
    props: CommunityDevelopmentContainerProps
) => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();
    const [data, setData] = useState<CommunityDevelopmentDto>();
    const [noteId, setNotesId] = useState<string>(Constants.emptyGuid);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [showNotesDialog, setShowNotesDialog] = useState<boolean>(false);
    const [drawerType, setDrawerType] = React.useState<GridActionType[]>([
        GRID_ACTION_TYPES.HIDDEN,
    ]);
    const { setSearchAction, clearSearchAction } = useActionFromQuery(onCustomEvent);

    const communityDevelopmentQuery =
        CommunityDevelopmentService.getCommunityDevelopmentQuery(
            data?.id ?? undefined
        );
    const communityDevelopmentNoteQuery =
        CommunityDevelopmentService.getCommunityDevelopmentNoteQuery(
            noteId ?? undefined
        );
    const communityDevelopmentRepliesQuery =
        CommunityDevelopmentService.getCommunityDevelopmentRepliesQuery(
            data?.id ?? undefined
        );
    const communityDevelopmentLookupQuery =
        CommunityDevelopmentService.getLookups();

    const { mutateAsync: saveCommunityDevelopment } = CommunityDevelopmentService.saveCommunityDevelopment();
    const { mutateAsync: saveCommunityDevelopmentFavorite } = CommunityDevelopmentService.saveCommunityDevelopmentFavorite();
    const { mutateAsync: saveCommunityDevelopmentNote } = CommunityDevelopmentService.saveCommunityDevelopmentNote();
    const { mutateAsync: saveCommunityDevelopmentReply } = CommunityDevelopmentService.saveCommunityDevelopmentReply();
    const { mutate: deleteCommunityDevelopment } =
        CommunityDevelopmentService.deleteCommunityDevelopment();
    const { mutate: deleteCommunityDevelopmentNote } =
        CommunityDevelopmentService.deleteCommunityDevelopmentNote();

    let communityDevelopmentNotesData: CommunityDevelopmentNoteDto = {
        id: Constants.emptyGuid,
        isDeleted: false,
        submittedDate: new Date(),
        communityDevelopmentId: data?.id ?? Constants.emptyGuid,
    };
    const communityDevelopmentReplyData: CommunityDevelopmentReplyDto = {
        id: Constants.emptyGuid,
        isDeleted: false,
        communityDevelopmentId: data?.id ?? Constants.emptyGuid,
        submittedDate: new Date(),
    };
    function onCustomEvent(actionType: string, dataId: string) {
        if (isGridActionType(actionType)) {
            setSearchAction(actionType, dataId);
            setDrawerType([actionType]);
            setData({ id: dataId } as CommunityDevelopmentDto);
        }
        if (actionType == "delete") {
            setData({ id: dataId } as CommunityDevelopmentDto);
            setShowDialog(true);
        }
        if (actionType == GRID_ACTION_TYPES.EDIT) {
            setDrawerType([actionType]);
            setNotesId(Constants.emptyGuid);
            setData({ id: dataId } as CommunityDevelopmentDto);
            renderDevelopmentForm();
        }
        if (actionType == GRID_ACTION_TYPES.ADD_FAVORITE || actionType == GRID_ACTION_TYPES.FAVORITED) {
            setData({ id: dataId } as CommunityDevelopmentDto);
            const favDto: CommunityDevelopmentFavoriteDto = {
                communityDevelopmentId: dataId,
                isFavorite: actionType == GRID_ACTION_TYPES.ADD_FAVORITE ? true : false,
            };
            saveFavourite(favDto);
        }

        if (actionType == GRID_ACTION_TYPES.TAG) {
            setData({ id: dataId } as CommunityDevelopmentDto);
        }
    }

    function onDevelopmentNoteCustomEvent(actionType: string, dataId: string) {
        if (actionType == "delete") {
            setData({ id: Constants.emptyGuid } as CommunityDevelopmentDto);
            setNotesId(() => dataId);
            setShowNotesDialog(true);
        }
        if (actionType == GRID_ACTION_TYPES.EDIT) {
            setDrawerType([actionType]);
            setData({ id: Constants.emptyGuid } as CommunityDevelopmentDto);
            setNotesId(() => dataId);
            if (communityDevelopmentNoteQuery?.data) {
                communityDevelopmentNotesData = communityDevelopmentNoteQuery?.data;
            } else {
                communityDevelopmentNotesData.communityDevelopmentId =
                    data?.id ?? Constants.emptyGuid;
                communityDevelopmentNotesData.id = dataId;
            }

            renderNotesForm();
        }
    }

    const saveFavourite = async (values: CommunityDevelopmentFavoriteDto) => {
        return await saveCommunityDevelopmentFavorite(values, {
            onSuccess: () => {
                showSnackbar("Favorite Saved", MessageSeverity.Success);
                queryClient.invalidateQueries({
                    queryKey: ["communityDevelopmentQuery"],
                });
                queryClient.invalidateQueries({
                    queryKey: ["communityDevelopmentGridQuery"],
                });
            },
        });
    };

    const saveDevelopmentNote = async (values: CommunityDevelopmentNoteDto) => {
        return await saveCommunityDevelopmentNote(values, {
            onSuccess: () => {
                showSnackbar("Notes Saved", MessageSeverity.Success);
                queryClient.invalidateQueries({
                    queryKey: ["CommunityDevelopmentNotesQuery"],
                });
                queryClient.invalidateQueries({
                    queryKey: ["communityDevelopmentGridQuery"],
                });
            },
        });
    };

    const saveCommunityDevelopmentData = async (values: CommunityDevelopmentDto) => {
        return await saveCommunityDevelopment(values, {
            onSuccess: () => {
                showSnackbar("Community Saved", MessageSeverity.Success);
                queryClient.invalidateQueries({
                    queryKey: ["communityDevelopmentQuery"],
                });
                queryClient.invalidateQueries({
                    queryKey: ["communityDevelopmentGridQuery"],
                });
            },
        });
    };

    const closeDrawer = (drawertype?: GridActionType) => {
        if (drawertype) {
            const drawerArray = [...drawerType];
            const index = drawerArray.indexOf(drawertype);
            if (index != -1) {
                drawerArray.splice(index, 1);
                setDrawerType(drawerArray);
            } else {
                setDrawerType([GRID_ACTION_TYPES.HIDDEN]);
            }
        } else {
            setDrawerType([GRID_ACTION_TYPES.HIDDEN]);
        }
        clearSearchAction();
    };

    const handleDeleteNotes = () => {
        deleteCommunityDevelopmentNote(
            noteId && noteId != Constants.emptyGuid ? noteId : Constants.emptyGuid,
            {
                onSuccess: () => {
                    showSnackbar(
                        "Community Development Notes Deleted",
                        MessageSeverity.Success
                    );
                    queryClient.invalidateQueries({
                        queryKey: ["CommunityDevelopmentNotesQuery"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["communityDevelopmentGridQuery"],
                    });
                    closeDrawer();
                },
            }
        );
    };

    const handleDeleteCustomDevelopment = (communityId: string) => {
        deleteCommunityDevelopment(communityId, {
            onSuccess: () => {
                showSnackbar("Community Development Deleted", MessageSeverity.Success);
                queryClient.invalidateQueries({
                    queryKey: ["communityDevelopmentGridQuery"],
                });
                closeDrawer();
            },
        });
    };

    const renderDrawerContent = () => {
        const isDataLoaded =
            !!communityDevelopmentQuery?.data &&
            !!communityDevelopmentLookupQuery?.data;
        const isRepliesDataLoaded = !!communityDevelopmentRepliesQuery?.data;
        const isNoteDataLoaded = !!communityDevelopmentNoteQuery?.data;

        return drawerType.map((i: GridActionType, index: number) => {
            switch (i) {
                case GRID_ACTION_TYPES.VIEW_REPLY: {
                    return isRepliesDataLoaded ? (
                        <CommunityDevelopmentReplyView
                            key={i}
                            data={communityDevelopmentRepliesQuery?.data}
                            onHide={() => closeDrawer(GRID_ACTION_TYPES.VIEW_REPLY)}
                            show={true}
                        />
                    ) : null;
                }
                case GRID_ACTION_TYPES.NOTES: {
                    return !!communityDevelopmentLookupQuery?.data && (
                        <React.Fragment key={i}>{renderNotesForm()}</React.Fragment>
                    );
                }
                case GRID_ACTION_TYPES.EDIT: {
                    return (
                        <React.Fragment key={i}>
                            {noteId && noteId != Constants.emptyGuid ?
                                isNoteDataLoaded &&
                                renderNotesForm(communityDevelopmentNoteQuery?.data)
                                :
                                isDataLoaded &&
                                renderDevelopmentForm()}
                        </React.Fragment>
                    );
                }
                case GRID_ACTION_TYPES.SEND_REPLY: {
                    return !!communityDevelopmentLookupQuery?.data && (
                        <React.Fragment key={i}>{renderReplyForm()}</React.Fragment>
                    );
                }
            }
        });
    };

    const renderDevelopmentForm = () => {
        return (
            <>
                <CommunityDevelopmentForm
                    data={communityDevelopmentQuery?.data}
                    show={!!communityDevelopmentQuery?.data}
                    onHide={() => closeDrawer(GRID_ACTION_TYPES.VIEW)}
                    lookups={communityDevelopmentLookupQuery?.data}
                    onSave={saveCommunityDevelopmentData}
                />
            </>
        );
    };

    const renderNotesForm = (data?: any) => {
        return (
            <CommunityDevelopmentNotesForm
                data={data ?? communityDevelopmentNotesData}
                show={true}
                onHide={() => closeDrawer(GRID_ACTION_TYPES.VIEW)}
                onSave={saveDevelopmentNote}
            />
        );
    };

    const renderReplyForm = () => {
        return (
            drawerType.includes(GRID_ACTION_TYPES.SEND_REPLY) && (
                <CommunityDevelopmentReplyForm
                    data={communityDevelopmentReplyData}
                    show={true}
                    onHide={() => closeDrawer(GRID_ACTION_TYPES.VIEW)}
                    onSave={saveCommunityDevelopmentReply}
                />
            )
        );
    };

    return (
        <div>
            {communityDevelopmentLookupQuery?.isLoading ? (
                <LoadingIndicator isFullPage={true} />
            ) : (
                <CommunityDevelopmentPage
                    communityId={props.communityId}
                    onCustomEvent={onCustomEvent}
                    lookups={communityDevelopmentLookupQuery?.data}
                    onDevelopmentNoteCustomEvent={onDevelopmentNoteCustomEvent}
                />
            )}

            {renderDrawerContent()}

            {
                <Dialog
                    contentText={"Are you sure you want to delete?"}
                    contentTitle={"Delete"}
                    onClose={() => setShowDialog(false)}
                    onConfirm={() => {
                        setShowDialog(false);
                        handleDeleteCustomDevelopment(data?.id ?? Constants.emptyGuid);
                    }}
                    open={showDialog}
                    closeText={"No, Go Back"}
                    confirmText={"Yes, Continue"}
                />
            }
            {
                <Dialog
                    contentText={"Are you sure you want to delete?"}
                    contentTitle={"Delete"}
                    onClose={() => setShowNotesDialog(false)}
                    onConfirm={() => {
                        setShowNotesDialog(false);
                        handleDeleteNotes();
                    }}
                    open={showNotesDialog && !showDialog}
                    closeText={"No, Go Back"}
                    confirmText={"Yes, Continue"}
                />
            }
        </div>
    );
};